import React, { useEffect, useState } from 'react';
import { FieldState } from '../../../../types/generic';
import { createOpInitialState } from '../../../../utils/initialStates';
import { addError, checkRequired, handleFieldValueUpdate } from '../../../../utils/form';
import FormTextField from '../../../Form/FormTextField';
import UserSelect from '../../../UserSelect';
import { UserDetails } from '../../../../types/user';
import { Button, CircularProgress, Stack } from '@mui/material';
import AlertDialog from '../../../AlertDialog';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { callAPISync, checkOperationExists, createOperation } from '../../../../utils/api';
import { addNewRole } from '../../../../store/user/userSlice';
import SubmissionAlert from '../../../SubmissionAlert';
import { removeOpSuffix } from '../../../../utils/operations';
import { addUserRoles } from '../../../../store/tab/tabSlice';
import { useLogto } from '@logto/react';

const INVALID_NAME_CHARS = /[^a-z\d-]/gi;

const CreateOperation: React.FC = () => {
	const dispatch = useAppDispatch();
	const logto = useLogto();

	const userDetails = useAppSelector((state) => state.user);

	const [fieldValues, setFieldValues] = useState<FieldState>(createOpInitialState);
	const [selectedUploadUsers, setSelectedUploadUsers] = useState<Record<string, UserDetails>>({});
	const [selectedViewUsers, setSelectedViewUsers] = useState<Record<string, UserDetails>>({});
	const [showNoUserConfirm, setShowNoUserConfirm] = useState<boolean>(false);

	const [submitting, setSubmitting] = useState<boolean>(false);
	const [submissionSuccess, setSubmissionSuccess] = useState<boolean>(false);
	const [submissionMessage, setSubmissionMessage] = useState<string[]>([]);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		if (name === 'operationName' && INVALID_NAME_CHARS.test(value)) {
			handleFieldValueUpdate(name, value.replace(INVALID_NAME_CHARS, ''), fieldValues, setFieldValues);
		} else {
			handleFieldValueUpdate(name, value, fieldValues, setFieldValues);
		}
	};

	const operationAPIUtil = (opName: string, opDesc: string, allUsers: Record<string, UserDetails>) => {
		callAPISync(logto, (token) => {
			createOperation(token, opName, opDesc, Object.keys(allUsers))
				.then(async (createResp) => {
					if (createResp.ok) {
						const createdUsers = (await createResp.json()) as { assignedUsers: string[] };
						setSubmissionSuccess(true);
						setSubmissionMessage([
							`Successfully created operation "${removeOpSuffix(opName)}"`,
							`${createdUsers.assignedUsers.length} user(s) assigned`,
						]);
						setSelectedUploadUsers({});
						setSelectedViewUsers({});
						setFieldValues({ ...createOpInitialState });
						setSubmitting(false);
					} else {
						setSubmissionSuccess(false);
						setSubmissionMessage([
							`Encountered an error when attempting to create "${removeOpSuffix(opName)}"`,
							`${createResp.status}: ${createResp.statusText}`,
							`Try again or contact an administrator if this persists`,
						]);
						setSubmitting(false);
					}
				})
				.catch((e) => {
					setSubmissionSuccess(false);
					setSubmissionMessage([
						`Encountered an unhandled error while attempting to create "${removeOpSuffix(opName)}"`,
						`${String(e)}`,
						`Try again or contact an administrator if this persists`,
					]);
					setSubmitting(false);
				});
		});
	};

	const createOp = () => {
		const allUploadUsers = { ...selectedUploadUsers };
		const allViewUsers = { ...selectedViewUsers };

		// assign current user to both upload and view if none are selected
		if (Object.keys(allUploadUsers).length === 0 || Object.keys(allViewUsers).length === 0) {
			allUploadUsers[userDetails.id] = userDetails;
			allViewUsers[userDetails.id] = userDetails;
		}
		const opName = (fieldValues['operationName'].value as string).toLocaleUpperCase();
		const opDesc = fieldValues['operationDescription'].value as string;

		operationAPIUtil(`${opName}-upload`, opDesc, allUploadUsers);
		operationAPIUtil(`${opName}-view`, opDesc, allViewUsers);

		if (Object.values(allUploadUsers).some((user) => user.email === userDetails.email)) {
			dispatch(addNewRole(`${opName}-upload`));
			dispatch(addUserRoles(['UPLOAD']));
		}

		if (Object.values(allViewUsers).some((user) => user.email === userDetails.email)) {
			dispatch(addNewRole(`${opName}-view`));
			dispatch(addUserRoles(['VIEW']));
		}
	};

	const onSubmit = () => {
		const tempVals = { ...fieldValues };
		setSubmitting(true);
		setSubmissionMessage([]);
		checkRequired(tempVals);

		const opName = fieldValues['operationName'].value as string;
		if (opName) {
			if (opName.startsWith(' ')) {
				addError(tempVals['operationName'], 'Operation name cannot start with a space');
			} else if (opName.length < 5) {
				addError(tempVals['operationName'], 'Operation name must be at least 5 characters');
			}
		}

		if (INVALID_NAME_CHARS.test(opName)) {
			addError(tempVals['operationName'], 'Operation name contains invalid characters');
		}

		if (opName.startsWith('-') || opName.endsWith('-')) {
			addError(tempVals['operationName'], 'Operation name cannot start or end with a hyphen');
		}

		if (Object.values(fieldValues).filter((val) => val.error === true).length === 0) {
			callAPISync(logto, (token) => {
				checkOperationExists(token, `${opName}-upload`)
					.then((operationExists) => {
						if (operationExists.status === 200) {
							addError(tempVals['operationName'], 'Operation name already exists');
							setSubmitting(false);
							setFieldValues({ ...tempVals });
						} else {
							if (Object.keys(selectedUploadUsers).length === 0 && Object.keys(selectedUploadUsers).length === 0) {
								setShowNoUserConfirm(true);
							} else {
								createOp();
							}
						}
					})
					.catch((e) => {
						setSubmissionSuccess(false);
						setSubmissionMessage([`Encountered an unhandled error while attempting to create "${opName}"`, `${String(e)}`]);
						setSubmitting(false);
					});
			});
		} else {
			setSubmitting(false);
			setFieldValues({ ...tempVals });
		}
	};

	useEffect(() => {
		if (submissionMessage && Object.values(selectedUploadUsers).length === 0 && Object.keys(selectedViewUsers).length === 0) {
			window.scrollTo(0, 0);
		}
	}, [submissionMessage]);

	return (
		<Stack
			sx={{
				width: '100%',
			}}
			spacing={2}
			data-testid="operations-tab-content"
			flex={1}
		>
			<SubmissionAlert
				showCondition={submissionMessage.length > 0}
				success={submissionSuccess}
				onClose={() => setSubmissionMessage([])}
				messages={submissionMessage}
			/>

			<FormTextField
				fieldData={fieldValues.operationName}
				textFieldProps={{
					label: 'Operation Name',
					name: 'operationName',
					onChange: handleInputChange,
				}}
			/>

			<FormTextField
				fieldData={fieldValues.operationDescription}
				textFieldProps={{
					label: 'Operation Description',
					name: 'operationDescription',
					onChange: handleInputChange,
				}}
			/>

			<UserSelect
				operationType="CREATE"
				selectedUploadUsers={selectedUploadUsers}
				selectedViewUsers={selectedViewUsers}
				setSelectedUploadUsers={setSelectedUploadUsers}
				setSelectedViewUsers={setSelectedViewUsers}
			/>

			<Button variant="contained" disabled={submitting} onClick={() => onSubmit()} size="large" data-testid="create-operation-submit">
				{submitting ? <CircularProgress color="inherit" /> : 'Create Operation'}
			</Button>

			<AlertDialog
				title="No users assigned to either upload or view permissions in operation"
				message={[
					`No users have been assigned to operation ${fieldValues['operationName'].value}`,
					'If you create it, you will be automatically assigned upload and visualise permissions as the only user',
					'Continue?',
				]}
				isOpen={showNoUserConfirm}
				rightButtonMessage={'Cancel'}
				rightButtonAction={() => {
					setSubmitting(false);
					setShowNoUserConfirm(false);
				}}
				leftButtonMessage={'Confirm'}
				leftButtonAction={() => {
					setShowNoUserConfirm(false);
					createOp();
				}}
			/>
		</Stack>
	);
};

export default CreateOperation;
